import { Box, Card, CardContent, Chip, Grid, TextField, Typography } from '@mui/material'
import React from 'react'
import {
  CustomerDto,
  CustomerPreferencesDto,
  OrganizationTypeDto,
  OrganizationTypeDtoType,
} from '../../../interactors/gen/backendClient'
import { AssociationBureauMembers } from './AssociationBureauMembers'
import { AssociationDescription } from './AssociationDescription'
import { AssociationTheAssociationCard } from './AssociationTheAssociationCard'
import { AxiosError } from 'axios'
import { UseMutationResult } from 'react-query'
import { AssociationPermissions } from './AssociationPermissions'
import { useAccountsStore } from '../../../store/AccountsStore'
import { useMemberAccessPolicy } from '../../../utils/hooks/useMemberAccessPolicy'
import { StripeConnectButton } from '../../Adopters/StripeConnectButton'
import { addPrimaryCtaSx } from '../../common/addPrimaryCtaSx'
import { CustomersClient } from '../../../interactors/clients/CustomerClient'
import { backendClient } from '../../../interactors/clients/client'
import { AssociationFullExportCard } from './AssociationFullExportCard'

interface AssociationProps {
  association: CustomerDto
  editAssociationMutation: UseMutationResult<CustomerDto, Error | AxiosError<unknown, any>, CustomerDto, unknown>
}

export const AssociationMyAssociationTab: React.FC<AssociationProps> = ({ association, editAssociationMutation }) => {
  const accountsStore = useAccountsStore()
  const [stripeContactEmail, setStripeContactEmail] = React.useState(association?.email || '')
  const [stripeContactEmailError, setStripeContactEmailError] = React.useState('')
  const { policy } = useMemberAccessPolicy(
    accountsStore.connectedAccount?.customerId,
    Boolean(accountsStore.connectedAccount)
  )

  React.useEffect(() => {
    setStripeContactEmail(association?.email || '')
  }, [association?.email])

  //Global submit function used by all the association form components (not the owner form)
  const onSubmit = async (
    data: Partial<CustomerDto> & Partial<CustomerPreferencesDto> & Partial<OrganizationTypeDto>
  ) => {
    const organizationType = {
      type: data.type || association.organizationType?.type,
      otherField:
        data.type === OrganizationTypeDtoType.OtherOrganizations
          ? data?.otherField || association.organizationType?.otherField
          : undefined,
    }
    const preferences = {
      species: data.species || association.preferences.species,
    }
    console.log('On submit', data)

    const { species, ...restData } = data

    const newData = {
      ...association,
      ...restData,
      organizationType: organizationType,
      preferences: preferences,
      phoneNumber: ['+33', ''].includes(data.phoneNumber?.trim() || '') ? undefined : data.phoneNumber,
    }
    console.log('newData', newData)
    editAssociationMutation.mutate(newData)
  }

  if (!association) {
    return null
  }

  const stripeContactEmailTrimmed = stripeContactEmail.trim()
  const isStripeContactEmailValid = /\S+@\S+\.\S+/.test(stripeContactEmailTrimmed)

  // Retention nudge: free organizations only, never Essential or Premium. Hidden while the backend does not send `plan`.
  const shouldDisplayStripeRetentionCard = Boolean(
    accountsStore.isAdmin() &&
      policy?.plan === 'free' &&
      policy?.trial.isEligibleForNewPolicy &&
      !policy?.stripeConnect.validatedAt &&
      policy?.trial.endsAt
  )
  const shouldDisplayStripeTrackingCard = Boolean(accountsStore.isAdmin() && policy?.stripeConnect.validatedAt)
  const isMasterAdmin =
    accountsStore.connectedAccount?.permission === 'administrator' &&
    accountsStore.connectedAccount?.id === association.masterAccountId

  return (
    <>
      {shouldDisplayStripeRetentionCard && (
        <Card
          sx={{
            mb: 2,
            backgroundColor: 'rgba(255, 248, 226, 0.78)',
            borderColor: 'rgba(243, 172, 0, 0.18)',
            boxShadow: 'none',
          }}
        >
          <CardContent sx={{ py: 2.2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.75 }}>
                  <Chip
                    label={
                      policy?.trial.isActive
                        ? `Plus que ${Math.max(
                            policy?.trial.daysRemaining || 0,
                            0
                          )} jour(s) pour finaliser la connexion`
                        : 'Votre période d’essai de 3 mois est terminée'
                    }
                    sx={{
                      height: 28,
                      borderRadius: '999px',
                      backgroundColor: '#E7F0FA',
                      color: '#1F3764',
                      fontSize: 12.5,
                      fontWeight: 700,
                      '& .MuiChip-label': {
                        px: 1.5,
                      },
                    }}
                  />
                  <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: 15.5, sm: 16 }, color: '#203864' }}>
                    Générez des liens de paiement pour les frais d’adoption
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    mt: 0.5,
                    color: '#4F5F7C',
                    fontSize: 13.5,
                    lineHeight: 1.45,
                  }}
                >
                  Centralisez la réception des paiements d&#39;adoption, sans commission.
                </Typography>
                <Typography
                  sx={{
                    mt: 0.35,
                    color: '#4F5F7C',
                    fontSize: 13.5,
                    lineHeight: 1.45,
                  }}
                >
                  Stripe est un partenaire de paiement pour garantir la réception des frais d&#39;adoptions conformes et
                  sécurisés.
                </Typography>
                {policy?.stripeConnect.currentlyUnavailable && (
                  <Typography sx={{ mt: 0.75, color: '#B98507', fontWeight: 700, fontSize: 12.5 }}>
                    Votre compte Stripe Connect a déjà été validé mais semble actuellement indisponible.
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  alignItems: { xs: 'stretch', md: 'center' },
                  gap: 1.25,
                  minWidth: { md: 500 },
                  alignSelf: { xs: 'stretch', md: 'center' },
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Email de l'asso"
                  placeholder={association?.email || "Email de l'association"}
                  value={stripeContactEmail}
                  onChange={(event) => {
                    setStripeContactEmail(event.target.value)
                    if (stripeContactEmailError) {
                      setStripeContactEmailError('')
                    }
                  }}
                  error={Boolean(stripeContactEmailError)}
                  helperText={stripeContactEmailError}
                  sx={{
                    flex: 1,
                    minWidth: { md: 250 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px',
                      backgroundColor: '#FFFFFF',
                    },
                  }}
                />
                <StripeConnectButton
                  variant="contained"
                  color="primary"
                  fullWidth={false}
                  content="Configurer Stripe Connect"
                  onClick={async () => {
                    if (!stripeContactEmailTrimmed || !isStripeContactEmailValid) {
                      setStripeContactEmailError("L'email de l'association est requis.")
                      return
                    }

                    const updatedCustomer =
                      association.email === stripeContactEmailTrimmed
                        ? association
                        : await CustomersClient.editCustomer({
                            ...association,
                            email: stripeContactEmailTrimmed,
                          })

                    accountsStore.changeConnectedCustomer(updatedCustomer)
                    const resLink = await backendClient.get('/customers/stripe-connect-link')
                    window.open(resLink.data, '_blank')
                  }}
                  sx={{
                    ...addPrimaryCtaSx,
                    minHeight: 42,
                    px: 3,
                    whiteSpace: 'nowrap',
                    alignSelf: { xs: 'flex-start', md: 'center' },
                    flexShrink: 0,
                    boxShadow: '0 10px 22px rgba(243, 172, 0, 0.18)',
                  }}
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}
      {shouldDisplayStripeTrackingCard && (
        <Card
          sx={{
            mb: 2,
            backgroundColor: 'rgba(236, 244, 253, 0.86)',
            borderColor: 'rgba(105, 141, 172, 0.18)',
            boxShadow: 'none',
          }}
        >
          <CardContent sx={{ py: 2.2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={800} sx={{ fontSize: { xs: 15.5, sm: 16 }, color: '#203864' }}>
                  Suivez la réception de vos frais d’adoption
                </Typography>
                <Typography
                  sx={{
                    mt: 0.5,
                    color: '#4F5F7C',
                    fontSize: 13.5,
                    lineHeight: 1.45,
                  }}
                >
                  Accédez à votre espace Stripe pour suivre les paiements, les virements et l’état de vos encaissements.
                </Typography>
                {policy?.stripeConnect.currentlyUnavailable && (
                  <Typography sx={{ mt: 0.75, color: '#B98507', fontWeight: 700, fontSize: 12.5 }}>
                    Votre compte Stripe Connect semble actuellement indisponible.
                  </Typography>
                )}
              </Box>
              <StripeConnectButton
                variant="contained"
                color="primary"
                fullWidth={false}
                content="Accéder à mon espace Stripe"
                onClick={async () => {
                  if (!association.stripeCustomerId) {
                    return
                  }

                  const { data: session } = await backendClient.post('/stripe/create-portal-session-customer-id', {
                    customerId: association.stripeCustomerId,
                  })
                  window.location.href = session
                }}
                sx={{
                  ...addPrimaryCtaSx,
                  minHeight: 42,
                  px: 3,
                  whiteSpace: 'nowrap',
                  alignSelf: { xs: 'flex-start', md: 'center' },
                  flexShrink: 0,
                }}
              />
            </Box>
          </CardContent>
        </Card>
      )}
      <AssociationDescription association={association!} onSubmit={onSubmit} />
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <AssociationPermissions association={association} onSubmit={onSubmit} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <AssociationTheAssociationCard association={association} onSubmit={onSubmit} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <AssociationBureauMembers association={association!} onSubmit={onSubmit} />
        </Grid>
      </Grid>
      {isMasterAdmin && <AssociationFullExportCard />}
    </>
  )
}
